import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { QRCodeCanvas, QRCodeSVG } from "qrcode.react";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import { Button, ButtonGroup, Card, CardBody, Col, Container, Input, Row, Spinner } from "reactstrap";
import { ErrorToaster, successToaster } from "components/common/toast";
import { publicUrl } from "components/common/EmbedSnippets";
import { PRESETS, readableContrast } from "components/common/embedCode";
import { styleKind } from "views/menu-cards/cardKind";
import { getRequest } from "network/ApiRequest";
import { GetBookingSettingsAPI, SaveBookingSettingsAPI } from "network/bookings";
import { CORNERS, FIELD_KEYS, FIELD_STATES, buttonSpot, dragToPercent, fullForm, withPreset } from "./bookingStyleLogic";
import { DeleteStyleImageAPI, GetStylesAPI, ResetStyleAPI, SaveStyleAPI, UploadStyleImageAPI } from "network/cardStyle";

// Aree del menu: ogni pagina Style mostra solo i tipi di carta della sua area.
const AREAS = {
  drinks: { title: "Drinks Style", kinds: ["wine", "beer", "spirits", "cocktail"] },
  food: { title: "Food Style", kinds: ["menu"] },
  booking: { title: "Bookings Style", kinds: ["booking"] },
  order: { title: "Orders Style", kinds: ["order"] },
};
// I nomi dei gruppi e dei tipi di carta si traducono in render (style.groups.*, style.kinds.*).
const GROUPS = ["button", "category", "subcategory", "product"];
const FALLBACK_FONTS = ["Montserrat", "Playfair Display", "Lora", "Open Sans", "Poppins", "Cormorant Garamond", "DM Sans", "Roboto", "Raleway", "Merriweather", "Outfit", "Libre Baskerville"];
const DEFAULT_GROUP = { color: "#3A3733", textColor: "#FFFFFF", style: "solid", size: "medium", radius: "8", font: "", italic: false };
const POSITIONS = ["top", "center", "bottom"];
const WHERE = ["inline", "right", "left", "center", "trigger", "page"];

const esc = (v) => String(v).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const full = (kind, g) => {
  const b = { ...DEFAULT_GROUP, ...(g?.button || {}) };
  return {
    button: { ...b, label: b.label ?? "", position: b.position || "bottom" },
    category: { ...DEFAULT_GROUP, ...(g?.category || {}) },
    subcategory: { ...DEFAULT_GROUP, ...(g?.subcategory || {}) },
    product: { ...DEFAULT_GROUP, ...(g?.product || {}), color: g?.product?.color || "#FFFFFF", textColor: g?.product?.textColor || "#3A3733" },
  };
};
const radiusCss = (r) => (r === "pill" ? "999px" : `${Number(r) || 0}px`);
const sizeCss = { small: { pad: "6px 14px", fs: 12 }, medium: { pad: "9px 20px", fs: 14 }, large: { pad: "12px 28px", fs: 16 } };
const look = (g) => ({
  background: g.style === "outline" ? "transparent" : g.color,
  color: g.style === "outline" ? g.color : g.textColor,
  border: `2px solid ${g.color}`,
  borderRadius: radiusCss(g.radius),
  fontFamily: g.font ? `'${g.font}', serif` : undefined,
  fontStyle: g.italic ? "italic" : "normal",
  fontSize: sizeCss[g.size].fs,
  padding: sizeCss[g.size].pad,
  fontWeight: 600,
});

// Codice HTML (solo link, nessuno script) con lo stesso aspetto del bottone scelto.
const buttonHtml = (href, label, g) => {
  const fg = g.style === "outline" ? g.color : g.textColor;
  const bg = g.style === "outline" ? "transparent" : g.color;
  const font = g.font ? `'${g.font}',serif` : "system-ui,sans-serif";
  const fs = sizeCss[g.size].fs;
  const css = `display:inline-block;padding:${sizeCss[g.size].pad};border:2px solid ${g.color};border-radius:${radiusCss(g.radius)};background:${bg};color:${fg};font:${g.italic ? "italic " : ""}600 ${fs}px ${font};text-decoration:none`;
  return `<a href="${href}" target="_blank" rel="noopener" style="${css}">${esc(label)}</a>`;
};
const fontLink = (fonts) => {
  const list = [...new Set(fonts.filter(Boolean))];
  return list.length
    ? `<link rel="stylesheet" href="https://fonts.googleapis.com/css2?${list.map((f) => `family=${f.replace(/ /g, "+")}:ital,wght@0,600;1,600`).join("&")}&display=swap">\n`
    : "";
};

const copy = (text) =>
  (navigator.clipboard ? navigator.clipboard.writeText(text) : Promise.reject())
    .then(() => successToaster(i18n.t("cards:style.copied")))
    .catch(() => ErrorToaster(i18n.t("cards:embed.copyFailed")));

const download = (href, name) => {
  const a = document.createElement("a");
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

const Segmented = ({ value, options, onChange }) => (
  <ButtonGroup size="sm">
    {options.map(([v, text]) => (
      <Button key={v} color={value === v ? "primary" : "secondary"} outline={value !== v} onClick={() => onChange(v)}>{text}</Button>
    ))}
  </ButtonGroup>
);

const Field = ({ label, children }) => (
  <div className="mb-3">
    <label className="d-block mb-1">{label}</label>
    {children}
  </div>
);

export default function StylePage() {
  const { t } = useTranslation("cards");
  const [params] = useSearchParams();
  const area = AREAS[params.get("area")] ? params.get("area") : "drinks";
  const kinds = AREAS[area].kinds;
  const [cards, setCards] = useState(null);
  const [data, setData] = useState(null); // risposta dell'API: styles, fonts, kinds
  const [kind, setKind] = useState(kinds[0]);
  const [groups, setGroups] = useState(() => full("wine"));
  const [image, setImage] = useState("");
  const [tab, setTab] = useState("button");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [device, setDevice] = useState("phone");
  const [view, setView] = useState("cover");
  const [where, setWhere] = useState("inline");
  const [cardId, setCardId] = useState("");
  const [bs, setBs] = useState(null); // impostazioni prenotazioni: link pubblico, slug, stile del modulo
  const [form, setForm] = useState(fullForm());
  const [welcome, setWelcome] = useState("");
  const dragging = useRef(false);
  const coverRef = useRef(null);
  const fileRef = useRef(null);
  const canvasRef = useRef(null);
  const svgRef = useRef(null);

  useEffect(() => {
    getRequest("menucard/requests")
      .then((res) => setCards((res?.data?.menu_cards || []).filter((c) => c.is_active)))
      .catch(() => setCards([]));
    GetStylesAPI()
      .then((res) => setData(res?.data || { styles: {}, fonts: FALLBACK_FONTS }))
      .catch(() => setData({ styles: {}, fonts: FALLBACK_FONTS }));
  }, []);

  // Prenotazioni: link pubblico e stile del modulo vengono dalle impostazioni.
  useEffect(() => {
    if (area !== "booking") return;
    GetBookingSettingsAPI()
      .then((s) => {
        setBs(s);
        setForm(fullForm(s.form_style));
        setWelcome(s.welcome_message || "");
      })
      .catch(() => {});
  }, [area]);

  // Cambio area dal menu: si parte dal primo tipo dell'area.
  useEffect(() => {
    setKind(AREAS[area].kinds[0]);
    setCardId("");
  }, [area]);

  // Cambio tipo di carta: si carica lo stile salvato per quel tipo.
  useEffect(() => {
    if (!data) return;
    const saved = data.styles?.[kind];
    setGroups(full(kind, saved?.style));
    setImage(saved?.image || "");
    setDirty(false);
  }, [data, kind]);

  const fonts = data?.fonts?.length ? data.fonts : FALLBACK_FONTS;
  const kindCards = useMemo(() => (cards || []).filter((c) => styleKind(c) === kind), [cards, kind]);
  const card = kindCards.find((c) => c.uuid === cardId) || kindCards[0] || null;
  const isBooking = kind === "booking";
  const TABS = isBooking ? ["button", "form"] : GROUPS;
  const g = groups[tab] || groups.button;
  const label = groups.button.label || PRESETS[kind]?.label || "MENU";

  const set = (patch) => {
    setGroups((prev) => ({ ...prev, [tab]: { ...prev[tab], ...patch } }));
    setDirty(true);
  };
  const setF = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setDirty(true);
  };
  const applyToAll = () => {
    const { color, textColor, style, size, radius, font, italic } = g;
    setGroups((prev) => Object.fromEntries(GROUPS.map((k) => [k, { ...prev[k], color, textColor, style, size, radius, font, italic }])));
    setDirty(true);
    successToaster(t("style.appliedAll"));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await SaveStyleAPI(kind, groups);
      if (res?.success === false) throw new Error();
      if (isBooking) await SaveBookingSettingsAPI({ form_style: form, welcome_message: welcome });
      setData((d) => ({ ...d, styles: { ...d.styles, [kind]: res?.data || { style: groups, image } } }));
      setDirty(false);
      successToaster(t("style.saved"));
    } catch (e) {
      ErrorToaster(t("style.saveFailed"));
    }
    setSaving(false);
  };
  const reset = async () => {
    if (!window.confirm(t("style.resetConfirm"))) return;
    try {
      await ResetStyleAPI(kind);
      setData((d) => ({ ...d, styles: { ...d.styles, [kind]: undefined } }));
      setGroups(full(kind));
      setImage("");
      setDirty(false);
      successToaster(t("style.resetDone"));
    } catch (e) {
      ErrorToaster(t("style.opFailed"));
    }
  };
  const upload = async (file) => {
    if (!file) return;
    try {
      const res = await UploadStyleImageAPI(kind, file);
      const url = res?.data?.image;
      if (!url) throw new Error();
      setImage(url);
      setData((d) => ({ ...d, styles: { ...d.styles, [kind]: { ...(d.styles?.[kind] || {}), image: url } } }));
      successToaster(t("style.coverUploaded"));
    } catch (e) {
      ErrorToaster(t("style.badImage"));
    }
    if (fileRef.current) fileRef.current.value = "";
  };
  const removeImage = async () => {
    try {
      await DeleteStyleImageAPI(kind);
      setImage("");
      setData((d) => ({ ...d, styles: { ...d.styles, [kind]: { ...(d.styles?.[kind] || {}), image: "" } } }));
    } catch (e) {
      ErrorToaster(t("style.opFailed"));
    }
  };

  const ratio = readableContrast({ color: g.color, textColor: g.textColor, style: g.style });
  const canShare = isBooking ? !!bs : !!card;
  const link = isBooking ? (bs ? publicUrl(bs.public_path) : "") : card ? publicUrl(`menucard/public/${card.uuid}/page`) : "";
  const widgetUrl = isBooking ? (bs ? publicUrl(`bookings/public/${bs.slug}/widget.js`) : "") : card ? publicUrl(`menucard/public/${card.uuid}/widget.js`) : "";
  const singleHtml = canShare ? fontLink([groups.button.font]) + buttonHtml(link, label, groups.button) : "";
  const allHtml = useMemo(() => {
    if (!cards) return "";
    const all = cards.map((c) => {
      const k = styleKind(c);
      const b = full(k, data?.styles?.[k]?.style).button;
      return { c, b, text: b.label || PRESETS[k]?.label || c.name };
    });
    const fontsUsed = fontLink(all.map((x) => x.b.font));
    return fontsUsed + all.map((x) => buttonHtml(publicUrl(`menucard/public/${x.c.uuid}/page`), x.text, x.b)).join("\n");
  }, [cards, data]);
  const whereCode = (() => {
    if (!canShare) return "";
    if (where === "page") return `<div id="${isBooking ? "risto-booking" : "risto-menu"}"></div>\n<script src="${widgetUrl}" async></script>`;
    if (where === "trigger") return `<script src="${widgetUrl}" data-trigger="#il-tuo-pulsante" async></script>`;
    const b = groups.button;
    const a = [];
    if (where === "inline") a.push('data-mode="inline"');
    if (where === "left" || where === "center") a.push(`data-position="${where}"`);
    a.push(`data-label="${esc(label)}"`, `data-color="${b.color}"`);
    if (b.style === "outline") a.push('data-style="outline"');
    else a.push(`data-text-color="${b.textColor}"`);
    if (b.size !== "medium") a.push(`data-size="${b.size}"`);
    a.push(`data-radius="${b.radius}"`);
    return `<script src="${widgetUrl}" ${a.join(" ")} async></script>`;
  })();

  const downloadPng = () => {
    const c = canvasRef.current;
    if (c) download(c.toDataURL("image/png"), "qr-carta.png");
  };
  const downloadSvg = () => {
    const el = svgRef.current?.querySelector("svg");
    if (!el) return;
    const xml = new XMLSerializer().serializeToString(el);
    download(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`, "qr-carta.svg");
  };
  const printQr = () => {
    const el = svgRef.current?.querySelector("svg");
    if (!el) return;
    const w = window.open("", "_blank");
    if (!w) return ErrorToaster(t("style.popupBlocked"));
    w.document.write(`<html><head><title>QR code</title></head><body style="margin:0;display:flex;align-items:center;justify-content:center;height:100vh">${new XMLSerializer().serializeToString(el).replace("<svg", '<svg style="width:70vmin;height:70vmin"')}</body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  // Il pulsante si trascina dove si vuole sulla copertina (posizione libera in %).
  const startDrag = (e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
  };
  const moveDrag = (e) => {
    if (!dragging.current || !coverRef.current) return;
    const spot = dragToPercent({ x: e.clientX, y: e.clientY }, coverRef.current.getBoundingClientRect(), e.currentTarget.getBoundingClientRect());
    setGroups((prev) => ({ ...prev, button: { ...prev.button, ...spot } }));
    setDirty(true);
  };
  const endDrag = () => {
    dragging.current = false;
  };

  const spot = buttonSpot(groups.button);
  const width = device === "phone" ? 250 : 340;
  const height = device === "phone" ? 480 : 460;

  return (
    <div className="content">
      <Container fluid>
        <h2 className="mb-1">{AREAS[area].title}</h2>
        <p className="text-muted">{t("style.intro")}</p>

        <div className="mb-4 d-flex flex-wrap" style={{ gap: 8 }}>
          {kinds.length > 1 && kinds.map((k) => (
            <Button key={k} size="sm" color={k === kind ? "primary" : "secondary"} outline={k !== kind} style={{ borderRadius: 999 }} onClick={() => setKind(k)}>
              {t(`style.kinds.${k}`)}
            </Button>
          ))}
        </div>

        {!data || cards === null ? (
          <div className="text-center py-5"><Spinner /></div>
        ) : (
          <Row>
            <Col lg="6">
              <Card>
                <CardBody>
                  <h4>{t("style.cover")}</h4>
                  <p className="small text-muted">{t("style.coverHelp")}</p>
                  {image && <img src={image} alt="" style={{ height: 70, borderRadius: 8, marginRight: 12 }} />}
                  <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files[0])} />
                  <Button size="sm" onClick={() => fileRef.current?.click()}>{image ? t("style.changeImage") : t("style.uploadImage")}</Button>
                  {image && <Button size="sm" color="link" onClick={removeImage}>{t("style.remove")}</Button>}
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <div className="d-flex flex-wrap mb-3" style={{ gap: 6 }}>
                    {TABS.map((k) => (
                      <Button key={k} size="sm" color={k === tab ? "primary" : "secondary"} outline={k !== tab} onClick={() => { setTab(k); if (k === "form") setView("card"); else setView("cover"); }}>{t(`style.groups.${k}`)}</Button>
                    ))}
                  </div>

                  {isBooking && tab === "form" && (
                    <>
                      <Row>
                        <Col xs="6"><Field label={t("style.formAccent")}><Input type="color" value={form.accent} onChange={(e) => setF({ accent: e.target.value.toUpperCase() })} /></Field></Col>
                        <Col xs="6">
                          <Field label={t("style.formFont")}>
                            <Input type="select" value={form.ffont} onChange={(e) => setF({ ffont: e.target.value })}>
                              <option value="">{t("style.defaultFont")}</option>
                              {fonts.map((f) => <option key={f} value={f}>{f}</option>)}
                            </Input>
                          </Field>
                        </Col>
                      </Row>
                      <Field label={t("style.fieldCorners")}><Segmented value={form.fcorner} onChange={(v) => setF({ fcorner: v })} options={[["square", t("style.square")], ["round", t("style.rounded")], ["pill", t("style.pill")]]} /></Field>
                      <Field label={t("style.welcomeText")}><Input type="textarea" rows="2" value={welcome} onChange={(e) => { setWelcome(e.target.value); setDirty(true); }} /></Field>
                      <Field label={t("style.confirmText")}><Input type="textarea" rows="2" maxLength={200} value={form.confirm_message} onChange={(e) => setF({ confirm_message: e.target.value })} /></Field>
                      <p className="small text-muted mb-1">{t("style.fieldsHelp")}</p>
                      {FIELD_KEYS.map((k) => (
                        <div key={k} className="d-flex align-items-center justify-content-between mb-2">
                          <span>{t(`style.fields.${k}`)}</span>
                          <Input type="select" style={{ maxWidth: 170 }} value={form.fields[k]} onChange={(e) => setF({ fields: { ...form.fields, [k]: e.target.value } })}>
                            {FIELD_STATES.map((s) => <option key={s} value={s}>{t(`style.fieldState.${s}`)}</option>)}
                          </Input>
                        </div>
                      ))}
                      <Button color="primary" size="sm" disabled={saving || !dirty} onClick={save}>{saving ? t("style.saving") : t("save")}</Button>
                    </>
                  )}
                  {(!isBooking || tab !== "form") && (
                  <>

                  {tab === "button" && (
                    <Field label={t("style.buttonText")}>
                      <Input maxLength={40} placeholder={PRESETS[kind]?.label} value={groups.button.label} onChange={(e) => set({ label: e.target.value })} />
                    </Field>
                  )}
                  <Row>
                    <Col xs="6"><Field label={tab === "product" ? t("style.bgColor") : t("style.color")}><Input type="color" value={g.color} onChange={(e) => set({ color: e.target.value.toUpperCase() })} /></Field></Col>
                    <Col xs="6"><Field label={t("style.textColor")}><Input type="color" value={g.textColor} disabled={g.style === "outline"} onChange={(e) => set({ textColor: e.target.value.toUpperCase() })} /></Field></Col>
                  </Row>
                  {ratio !== null && ratio < 4.5 && (
                    <p className="small text-warning">{t("style.lowContrast", { ratio: ratio.toFixed(1) })}</p>
                  )}
                  <Field label={t("style.style")}><Segmented value={g.style} onChange={(v) => set({ style: v })} options={[["solid", t("style.solid")], ["outline", t("style.outline")]]} /></Field>
                  <Field label={t("style.size")}><Segmented value={g.size} onChange={(v) => set({ size: v })} options={[["small", t("style.small")], ["medium", t("style.medium")], ["large", t("style.large")]]} /></Field>
                  <Field label={t("style.corners")}><Segmented value={g.radius} onChange={(v) => set({ radius: v })} options={[["0", t("style.square")], ["8", t("style.rounded")], ["pill", t("style.pill")]]} /></Field>
                  <Row>
                    <Col xs="8">
                      <Field label="Font">
                        <Input type="select" value={g.font} onChange={(e) => set({ font: e.target.value })}>
                          <option value="">{t("style.defaultFont")}</option>
                          {fonts.map((f) => <option key={f} value={f}>{f}</option>)}
                        </Input>
                      </Field>
                    </Col>
                    <Col xs="4"><Field label={t("style.italic")}><Segmented value={g.italic ? "y" : "n"} onChange={(v) => set({ italic: v === "y" })} options={[["n", t("style.no")], ["y", t("style.yes")]]} /></Field></Col>
                  </Row>
                  {tab === "button" && (
                    <Field label={t("style.position")}><Segmented value={groups.button.position} onChange={(v) => { setGroups((prev) => ({ ...prev, button: withPreset(prev.button, v) })); setDirty(true); }} options={POSITIONS.map((p) => [p, t(`style.pos.${p}`)])} /></Field>
                  )}
                  <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                    <Button size="sm" color="secondary" outline onClick={applyToAll}>{t("style.applyAll")}</Button>
                    <Button size="sm" color="secondary" outline onClick={reset}>{t("style.reset")}</Button>
                    <Button color="primary" size="sm" disabled={saving || !dirty} onClick={save}>{saving ? t("style.saving") : t("save")}</Button>
                  </div>
                  </>
                  )}
                </CardBody>
              </Card>
            </Col>

            <Col lg="6">
              <Card>
                <CardBody>
                  <h4>{t("style.preview")}</h4>
                  <div className="d-flex flex-wrap mb-3" style={{ gap: 12 }}>
                    <Segmented value={device} onChange={setDevice} options={[["phone", t("style.devicePhone")], ["tablet", t("style.deviceTablet")]]} />
                    <Segmented value={view} onChange={setView} options={[["cover", t("style.viewCover")], ["card", t(isBooking ? "style.viewForm" : "style.viewCard")]]} />
                  </div>
                  <div className="d-flex justify-content-center">
                    <div style={{ width, height, borderRadius: 28, border: "8px solid #222", overflow: "hidden", position: "relative", background: "#fff" }}
                      ref={coverRef}>
                      {view === "cover" ? (
                        <>
                          <div style={{ position: "absolute", inset: 0, background: image ? `url(${image}) center/cover` : "linear-gradient(160deg,#6b5b4b,#2b2420)" }} />
                          <span title={t("style.dragHint")} onPointerDown={startDrag} onPointerMove={moveDrag} onPointerUp={endDrag} onPointerCancel={endDrag}
                            style={{ position: "absolute", left: `${spot.left}%`, top: `${spot.top}%`, transform: "translate(-50%,-50%)", whiteSpace: "nowrap", cursor: "grab", touchAction: "none", userSelect: "none", ...look(groups.button) }}>{label}</span>
                          <span style={{ position: "absolute", bottom: 4, left: 0, right: 0, textAlign: "center", fontSize: 10, color: "#fff", opacity: 0.7 }}>{t("style.dragHint")}</span>
                        </>
                      ) : isBooking ? (
                        <div style={{ padding: 14, color: "#3A3733", fontFamily: form.ffont ? `'${form.ffont}', sans-serif` : undefined, fontSize: 12, height: "100%", overflow: "auto" }}>
                          <div style={{ fontWeight: 600, fontSize: 16 }}>{bs?.slug || ""}</div>
                          <p style={{ margin: "2px 0 8px" }}>{welcome || t("style.welcomeDefault")}</p>
                          {[t("style.persons"), t("style.day"), t("style.time")].map((l) => (
                            <div key={l} className="mb-2">
                              <div style={{ fontWeight: 600 }}>{l}</div>
                              <div className="d-flex" style={{ gap: 4 }}>
                                {[1, 2, 3, 4].map((n) => (
                                  <span key={n} style={{ padding: "4px 9px", border: `1px solid ${n === 2 ? form.accent : "#cfc8b8"}`, background: n === 2 ? form.accent : "#fff", color: n === 2 ? "#fff" : "#333", borderRadius: CORNERS[form.fcorner] }}>{n}</span>
                                ))}
                              </div>
                            </div>
                          ))}
                          {["Nome e cognome *", "Telefono *", ...FIELD_KEYS.filter((k) => k !== "marketing" && form.fields[k] !== "off").map((k) => t(`style.fields.${k}`) + (form.fields[k] === "req" ? " *" : ""))].map((l) => (
                            <div key={l} style={{ border: "1px solid #cfc8b8", background: "#fff", padding: "6px 8px", marginBottom: 4, borderRadius: CORNERS[form.fcorner], color: "#777" }}>{l}</div>
                          ))}
                          {form.fields.marketing !== "off" && <label className="small d-block"><input type="checkbox" disabled /> {t("style.fields.marketing")}{form.fields.marketing === "req" ? " *" : ""}</label>}
                          <div style={{ marginTop: 8, textAlign: "center", color: "#fff", padding: "8px", background: form.accent, borderRadius: CORNERS[form.fcorner], fontWeight: 600 }}>{t("style.bookTable")}</div>
                          {form.confirm_message && <p className="small mt-2 mb-0 text-muted">“{form.confirm_message}”</p>}
                        </div>
                      ) : (
                        <div style={{ padding: 14, color: "#3A3733" }}>
                          <div style={{ ...look(groups.category), textAlign: "center", marginBottom: 8 }}>{t("style.sampleCategory")}</div>
                          <div style={{ ...look(groups.subcategory), display: "inline-block", marginBottom: 8 }}>Umbria</div>
                          {[["Sagrantino di Montefalco", "€ 48"], ["Barolo Riserva", "€ 65"]].map(([n, p]) => (
                            <div key={n} style={{ display: "flex", justifyContent: "space-between", padding: "10px 10px", marginBottom: 4, borderRadius: 6,
                              background: groups.product.style === "outline" ? "transparent" : groups.product.color,
                              border: groups.product.style === "outline" ? `1px solid ${groups.product.color}` : "none",
                              color: groups.product.style === "outline" ? groups.product.color : groups.product.textColor,
                              fontFamily: groups.product.font ? `'${groups.product.font}', serif` : undefined,
                              fontStyle: groups.product.italic ? "italic" : "normal", fontSize: sizeCss[groups.product.size].fs }}>
                              <span>{n}</span><span>{p}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <link rel="stylesheet" href={`https://fonts.googleapis.com/css2?${GROUPS.map((k) => groups[k].font).filter(Boolean).map((f) => `family=${f.replace(/ /g, "+")}:ital,wght@0,400;0,600;1,400;1,600`).join("&")}&display=swap`} hidden={!GROUPS.some((k) => groups[k].font)} />
                  <p className="small text-muted mt-2 mb-0">{t("style.previewNote")}</p>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <h4>{t("style.sharing")}</h4>
                  {kind === "order" ? (
                    <p className="text-muted">{t("style.shareElsewhere", { section: t(`style.sections.${kind}`) })}</p>
                  ) : !isBooking && kindCards.length === 0 ? (
                    <p className="text-muted">{t("style.noCardOfKind", { kind: t(`style.kinds.${kind}`) })}</p>
                  ) : (
                    <>
                      {!isBooking && kindCards.length > 1 && (
                        <Field label={t("style.card")}>
                          <Input type="select" value={card.uuid} onChange={(e) => setCardId(e.target.value)}>
                            {kindCards.map((c) => <option key={c.uuid} value={c.uuid}>{c.name}</option>)}
                          </Input>
                        </Field>
                      )}
                      <Row className="align-items-center mb-3">
                        <Col xs="4" className="text-center">
                          <div ref={svgRef}><QRCodeSVG value={link} size={110} /></div>
                          <div style={{ display: "none" }}><QRCodeCanvas ref={canvasRef} value={link} size={1024} includeMargin /></div>
                        </Col>
                        <Col xs="8">
                          <p className="small text-muted mb-2">{t("style.qrHelp")}</p>
                          <Button size="sm" className="mr-1" onClick={downloadPng}>PNG</Button>
                          <Button size="sm" className="mr-1" onClick={downloadSvg}>SVG</Button>
                          <Button size="sm" onClick={printQr}>{t("style.pdfPrint")}</Button>
                        </Col>
                      </Row>
                      <Field label={t("style.directLink")}>
                        <div className="d-flex" style={{ gap: 8 }}>
                          <Input readOnly value={link} onFocus={(e) => e.target.select()} />
                          <Button size="sm" onClick={() => copy(link)}>{t("embed.copy")}</Button>
                        </div>
                      </Field>
                      <Field label={t("style.htmlButton")}>
                        <Input type="textarea" readOnly rows={4} value={singleHtml} onFocus={(e) => e.target.select()} />
                        <Button size="sm" className="mt-1" onClick={() => copy(singleHtml)}>{t("embed.copy")}</Button>
                      </Field>
                      {!isBooking && (
                        <Field label={t("style.htmlAll")}>
                          <Input type="textarea" readOnly rows={5} value={allHtml} onFocus={(e) => e.target.select()} />
                          <Button size="sm" className="mt-1" onClick={() => copy(allHtml)}>{t("embed.copy")}</Button>
                        </Field>
                      )}
                      <Field label={t("embed.where.title")}>
                        <Input type="select" value={where} onChange={(e) => setWhere(e.target.value)}>
                          {WHERE.map((v) => <option key={v} value={v}>{t(`embed.where.${v}`)}</option>)}
                        </Input>
                        <Input type="textarea" readOnly rows={3} className="mt-2" value={whereCode} onFocus={(e) => e.target.select()} />
                        <Button size="sm" className="mt-1" onClick={() => copy(whereCode)}>{t("embed.copy")}</Button>
                      </Field>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
}
